import {
  Grid,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Grow,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useDishesBySnapshot } from '../dishes/api/getDishBySnapshot';
import { grey } from '@mui/material/colors';
import { useSnapshot } from '../snapshots/api/getSnapshot';
import { useUpdateSnapshot } from '../snapshots/api/updateSnapshot';
import { LoadingButton } from '@mui/lab';
import { Done, Save } from '@mui/icons-material';
import { useLocations } from '../locations/api/getLocations';

interface Props {
  selectedSnapshotId: number;
}

const SnapshotDetailsPanel = memo(({ selectedSnapshotId }: Props) => {
  const { data: connectedDish } = useDishesBySnapshot({
    snapshotId: selectedSnapshotId,
  });
  const { data: locations } = useLocations({});
  let locationName = locations?.find(
    (location) => location.id === connectedDish?.location_id
  )?.name;
  if (!locationName) {
    locationName = 'None';
  }
  const { data: snapshot } = useSnapshot({ snapshotId: selectedSnapshotId });
  const updateSnapshot = useUpdateSnapshot({});
  const [memoField, setMemoField] = useState('');
  const [loading, setLoading] = useState(false);
  const needToSaveMemo = memoField !== snapshot?.memo;

  useEffect(() => {
    setMemoField(snapshot?.memo || '');
  }, [snapshot?.memo]);

  const saveMemo = async () => {
    if (needToSaveMemo) {
      setLoading(true);
      await updateSnapshot.mutateAsync({
        data: { memo: memoField },
        snapshotId: selectedSnapshotId,
      });
      setLoading(false);
    }
  };

  return (
    <Grid item>
      <Grid item container justifyContent='center'>
        <Typography align='center'>Snapshot Details</Typography>
      </Grid>
      <TableContainer>
        <Table aria-label='simple table'>
          <TableBody>
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component='th' scope='row'>
                Connected Dish
              </TableCell>
              <TableCell>
                {connectedDish ? connectedDish.name : 'None'}
              </TableCell>
            </TableRow>
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component='th' scope='row'>
                Connected Satellites
              </TableCell>
              <TableCell>
                {connectedDish?.satellites
                  ?.map((satellite) => satellite.name)
                  .join(', ') || 'None'}
              </TableCell>
            </TableRow>
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component='th' scope='row'>
                Polarity
              </TableCell>
              <TableCell>
                {connectedDish?.polarity ? connectedDish.polarity : 'None'}
              </TableCell>
            </TableRow>
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component='th' scope='row'>
                Feed Name
              </TableCell>
              <TableCell>
                {connectedDish?.analyzer_dish &&
                connectedDish.analyzer_dish.length > 0
                  ? connectedDish?.analyzer_dish[0].feed_name
                  : 'None'}
              </TableCell>
            </TableRow>
            <TableRow
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component='th' scope='row'>
                Location
              </TableCell>
              <TableCell>{locationName}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Grid container item direction={'column'} spacing={1}>
        <Grid item>
          <Typography textAlign={'center'} color={grey[500]}>
            Memo
          </Typography>
        </Grid>
        <Grid item sx={{ mr: '1rem', ml: '1rem' }}>
          <TextField
            value={memoField}
            onChange={(e) => setMemoField(e.target.value)}
            multiline
            fullWidth
            rows={10}
          />
        </Grid>
        <Grid
          container
          item
          justifyContent={'flex-end'}
          spacing={1}
          alignItems={'center'}
          sx={{ pr: '1rem' }}
        >
          <Grid item>
            <LoadingButton
              type={'submit'}
              onClick={saveMemo}
              disabled={!needToSaveMemo}
              variant={'contained'}
              loading={loading}
              loadingPosition={'start'}
              startIcon={
                needToSaveMemo ? (
                  <Save />
                ) : (
                  <Grow in={!needToSaveMemo}>
                    <Done />
                  </Grow>
                )
              }
            >
              {needToSaveMemo ? 'Save' : 'Saved'}
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
});

export default SnapshotDetailsPanel;
